/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { BranchSettingsHubShell } from './BranchSettingsHubShell';
import { SettingsFormEditModal } from './components/SettingsFormEditModal';
import { BranchLocationPicker } from './components/BranchLocationPicker';
import { SettingsNavListCard } from './components/settingsScreenLayout';
import { SettingsNavigationRow } from '../components/SettingsNavigationRow';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketButton } from '../../shared/ticket/TicketButton'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader';
import { branchMissingMapLocation } from '../domain/branchLocationStatus';
import { patchSessionBranch } from '../../auth/infra/branches/branchSettingsSessionSync';

/** Hub: nombre + ubicación con mapa (único writer de address+lat/lng). */
export function BranchIdentityScreen() {
  return (
    <BranchSettingsHubShell>
      {(vm) => {
        const missing = branchMissingMapLocation(vm.currentBranch ?? {});
        const branchName = vm.currentBranch?.name ?? 'Sin nombre';
        const location = missing ? 'Sin ubicación' : (vm.currentBranch?.location ?? 'Sin ubicación');

        return (
          <View style={styles.stack} testID="branch.identity.screen">
            {/* Header Square */}
            <TicketModuleHeader
              kicker="Datos del local"
              title={branchName}
              meta={location}
              testID="branch.identity.header"
            />

            {/* Warning: falta ubicación */}
            {missing && vm.publicStoreFeaturesEnabled ? (
              <View style={styles.warningBanner} testID="branch.identity.warning">
                <View style={styles.warningDot} />
                <View style={styles.warningCopy}>
                  <Text style={styles.warningTitle}>Falta ubicar en el mapa</Text>
                  <Text style={styles.warningBody}>
                    Buscá la dirección abajo y confirmala en el mapa.
                  </Text>
                </View>
              </View>
            ) : null}

            {/* Identidad: nombre + editar */}
            <PaperSheet padded testID="branch.identity.name.sheet">
              <View style={styles.nameRow}>
                <View style={styles.nameCopy}>
                  <Text style={styles.nameLabel}>Nombre de esta sucursal</Text>
                  <Text style={styles.nameValue}>{branchName}</Text>
                  <Text style={styles.nameHint}>
                    Con más de una sucursal, usá barrio o referencia (ej. Palermo). En la tienda y la pantalla de
                    retiro mostramos tu marca y este nombre.
                  </Text>
                </View>
                {vm.canEditCommercial ? (
                  <TicketButton
                    variant="ghost"
                    label="Editar"
                    onPress={vm.openBranchIdentityEdit}
                    testID="branch.identity.name.edit"
                  />
                ) : null}
              </View>
            </PaperSheet>

            {/* Ubicación: mapa picker */}
            {vm.canEditCommercial && vm.currentBranchId ? (
              <BranchLocationPicker
                branchId={vm.currentBranchId}
                branchName={vm.currentBranch?.name ?? ''}
                branchAddress={vm.currentBranch?.location ?? ''}
                canEdit={vm.canEditCommercial}
                onSaved={(coords) => {
                  patchSessionBranch(vm.currentBranchId!, {
                    location: coords.address || vm.currentBranch?.location,
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                  });
                }}
              />
            ) : null}

            {/* Sucursales (dueño) */}
            {vm.openManageBranches ? (
              <SettingsNavListCard>
                <SettingsNavigationRow
                  title="Sucursales"
                  description="Alta, baja y reactivación."
                  onPress={vm.openManageBranches}
                  iconName="storefront-outline"
                  showSeparator={false}
                />
              </SettingsNavListCard>
            ) : null}

            {/* Modal editar nombre */}
            {vm.canEditCommercial ? (
              <SettingsFormEditModal
                visible={vm.branchIdentityEditOpen}
                title="Nombre de esta sucursal"
                fields={[
                  {
                    key: 'name',
                    label: 'Referencia de la sucursal',
                    value: vm.branchNameInput,
                    placeholder: 'Ej. Palermo o Quilmes',
                  },
                ]}
                saving={vm.branchIdentitySaving}
                onClose={vm.closeBranchIdentityEdit}
                onFieldChange={(key, value) => {
                  if (key === 'name') vm.setBranchNameInput(value);
                }}
                onSave={() => void vm.handleSaveBranchIdentity()}
              />
            ) : null}
          </View>
        );
      }}
    </BranchSettingsHubShell>
  );
}

const styles = StyleSheet.create({
  stack: {
    gap: 10,
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    backgroundColor: ticketColors.paper,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.gold,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  warningDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ticketColors.gold,
    marginTop: 5,
    flexShrink: 0,
  },
  warningCopy: {
    flex: 1,
    gap: 2,
  },
  warningTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
  warningBody: {
    fontSize: 13,
    color: ticketColors.ink2,
    lineHeight: 18,
    fontFamily: ticketFonts.sans,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  nameCopy: {
    flex: 1,
    gap: 2,
  },
  nameLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  nameValue: {
    fontSize: 17,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  nameHint: {
    marginTop: 4,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: '500',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
});
